import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { updateOrderInformation } from "@/api/UpdateOrderAPI";
import {
  ADVANCED_VERIFICATION_CHECKS,
  advancedVerificationGateway,
} from "../advanced-verification/AdvancedVerificationGateway";
import { legacyOrderAdapter } from "../adapters/legacyOrderAdapter";
import { legacyTrackingAdapter } from "../adapters/legacyTrackingAdapter";
import { legacyUploadAdapter } from "../adapters/legacyUploadAdapter";
import { DesktopMobileHandoff } from "../components/DesktopMobileHandoff";
import { ProgressIndicator } from "../components/ProgressIndicator";
import { VerificationLoadingOverlay } from "../components/VerificationLoadingOverlay";
import { AddressProofPreparationStep } from "../steps/AddressProofPreparationStep";
import { AddressProofStep } from "../steps/AddressProofStep";
import { DocumentCaptureStep } from "../steps/DocumentCaptureStep";
import { DocumentPreparationStep } from "../steps/DocumentPreparationStep";
import { DocumentReviewStep } from "../steps/DocumentReviewStep";
import { DocumentTypeStep } from "../steps/DocumentTypeStep";
import { ExpiredStep } from "../steps/ExpiredStep";
import { FailedStep } from "../steps/FailedStep";
import { FirstAttemptFailedStep } from "../steps/FirstAttemptFailedStep";
import { IntroStep } from "../steps/IntroStep";
import { LivenessPreparationStep } from "../steps/LivenessPreparationStep";
import { LivenessStep } from "../steps/LivenessStep";
import { ProcessingStep } from "../steps/ProcessingStep";
import { SuccessStep } from "../steps/SuccessStep";
import {
  documentRequiresBack,
  getAllowedDocumentTypes,
} from "../services/documentRequirements";
import { getVerificationReturnUrl } from "../services/returnUrlService";
import { verificationService } from "../services/verificationService";
import {
  KYC_ATTEMPT_STAGES,
  KYC_ATTEMPT_STATUS,
  legacyAttemptStorage,
  requiresSecondSubmission,
} from "./kycAttemptState";
import { kycProgressStorage } from "./kycProgressStorage";
import { createInitialKycState, kycReducer } from "./kycReducer";
import {
  DEVICE_CHECK_DELAY_MS,
  getDeviceContinuation,
} from "./deviceContinuation";
import { getSessionStep, KYC_STEPS } from "./kycSteps";

const PROCESSING_POLL_INTERVAL = 5000;
const MIN_STAGE_VALIDATION_MS = 2500;
const TERMINAL_STEPS = new Set([
  KYC_STEPS.SUCCESS,
  KYC_STEPS.FAILED,
  KYC_STEPS.EXPIRED,
]);

const revokeLocalPreview = (previewUrl) => {
  if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
};

const waitForMinimumValidationTime = (startedAt) => {
  const remaining = MIN_STAGE_VALIDATION_MS - (Date.now() - startedAt);
  return remaining > 0
    ? new Promise((resolve) => window.setTimeout(resolve, remaining))
    : Promise.resolve();
};

const restoreSessionProgress = (session, fallbackSessionId) => {
  const serverStep = getSessionStep(session);
  if (TERMINAL_STEPS.has(serverStep)) return session;

  const savedStep = kycProgressStorage.load(
    session?.sessionId || fallbackSessionId,
  );

  return savedStep ? { ...session, currentStep: savedStep } : session;
};

export function KycFlow({ session, sessionId, siteConfig }) {
  const { t } = useTranslation();
  const restoredSession = useMemo(
    () => restoreSessionProgress(session, sessionId),
    [session, sessionId],
  );
  const [state, dispatch] = useReducer(
    kycReducer,
    restoredSession,
    createInitialKycState,
  );
  const [busy, setBusy] = useState(false);
  const [validatingStage, setValidatingStage] = useState("");
  const [checkingDevice, setCheckingDevice] = useState(false);
  const [showDesktopHandoff, setShowDesktopHandoff] = useState(false);
  const deviceCheckTimerRef = useRef(null);

  useEffect(() => {
    dispatch({ type: "HYDRATE_SESSION", session: restoredSession });
  }, [restoredSession]);

  useEffect(() => {
    return () => {
      window.clearTimeout(deviceCheckTimerRef.current);
      deviceCheckTimerRef.current = null;
    };
  }, []);

  const currentSession = state.session || session || {};
  const verificationSessionId =
    sessionId ||
    currentSession.sessionId ||
    currentSession.verificationSessionId ||
    "";
  const allowedDocumentTypes = useMemo(
    () => getAllowedDocumentTypes(state.country),
    [state.country],
  );
  const requiresDocumentBack = documentRequiresBack(state.documentType);

  const checkDocumentAuthenticity = async (document) => {
    const result = await advancedVerificationGateway.verifyDocumentAuthenticity(
      {
        sessionId: verificationSessionId,
        orderId: currentSession.orderId || currentSession.orderNu || "",
        country: state.country,
        documentType: state.documentType,
        document,
      },
    );

    dispatch({
      type: "SET_ADVANCED_VERIFICATION",
      check: ADVANCED_VERIFICATION_CHECKS.DOCUMENT_AUTHENTICITY,
      value: result,
    });
    return result;
  };

  const checkBiometricLiveness = async (completedLiveness) => {
    const result = await advancedVerificationGateway.verifyBiometricLiveness({
      sessionId: verificationSessionId,
      orderId: currentSession.orderId || currentSession.orderNu || "",
      country: state.country,
      evidence: completedLiveness.value,
    });

    dispatch({
      type: "SET_ADVANCED_VERIFICATION",
      check: ADVANCED_VERIFICATION_CHECKS.BIOMETRIC_LIVENESS,
      value: result,
    });
    return result;
  };

  useEffect(() => {
    kycProgressStorage.save(verificationSessionId, state.currentStep);
  }, [state.currentStep, verificationSessionId]);

  useEffect(() => {
    if (state.currentStep !== KYC_STEPS.PROCESSING || !verificationSessionId) {
      return undefined;
    }

    let stopped = false;
    let timer;
    let controller;

    const poll = async () => {
      controller = new AbortController();

      try {
        const result = await verificationService.getVerificationStatus(
          verificationSessionId,
          controller.signal,
        );
        if (stopped) return;

        dispatch({ type: "SYNC_SESSION", session: result.session });

        if (result.status === "verified") {
          dispatch({ type: "SUBMIT_SUCCESS", value: result.session });
          return;
        }
        if (result.status === "failed") {
          dispatch({
            type: "SUBMIT_FAILURE",
            error: t("kyc.failed.description"),
          });
          return;
        }
        if (result.status === "expired") {
          dispatch({ type: "SESSION_EXPIRED" });
          return;
        }
      } catch (error) {
        if (error?.name === "AbortError" || stopped) return;
        // A temporary polling error must not fabricate a terminal result.
      }

      if (!stopped) timer = window.setTimeout(poll, PROCESSING_POLL_INTERVAL);
    };

    timer = window.setTimeout(poll, PROCESSING_POLL_INTERVAL);

    return () => {
      stopped = true;
      window.clearTimeout(timer);
      controller?.abort();
    };
  }, [state.currentStep, t, verificationSessionId]);

  const returnUrl = useMemo(
    () => getVerificationReturnUrl(currentSession),
    [currentSession],
  );

  const next = () => dispatch({ type: "NEXT" });
  const back = () => dispatch({ type: "BACK" });
  const returnToLivenessPreparation = () =>
    dispatch({ type: "GO_TO", step: KYC_STEPS.SELFIE });

  const handleIntroContinue = () => {
    if (deviceCheckTimerRef.current) return;

    setCheckingDevice(true);
    deviceCheckTimerRef.current = window.setTimeout(() => {
      deviceCheckTimerRef.current = null;
      if (getDeviceContinuation() === "mobile") {
        dispatch({ type: "NEXT" });
      } else {
        setShowDesktopHandoff(true);
      }
      setCheckingDevice(false);
    }, DEVICE_CHECK_DELAY_MS);
  };

  const persistStage = async (stage, nextState) => {
    const validationStartedAt = Date.now();
    setValidatingStage(stage);
    dispatch({ type: "ATTEMPT_SUBMIT_START", stage });

    try {
      const orderDetail = legacyOrderAdapter.toOrderDetail(
        currentSession,
        nextState,
      );
      const updatePayload = legacyOrderAdapter.toUpdateOrderPayload(
        orderDetail,
        siteConfig || currentSession.siteConfig,
      );
      const updateOrderSiteUrl =
        currentSession.siteUrl ||
        currentSession.site_url ||
        siteConfig?.siteUrl ||
        siteConfig?.site_url ||
        currentSession.siteConfig?.siteUrl ||
        currentSession.siteConfig?.site_url;

      await updateOrderInformation(updateOrderSiteUrl, updatePayload);
      const savedOrder = orderDetail;

      await waitForMinimumValidationTime(validationStartedAt);

      dispatch({ type: "SYNC_LEGACY_ORDER", value: savedOrder });

      const isFirstAttempt = state.attempts[stage]?.completedAttempts < 1;

      if (requiresSecondSubmission(stage) && isFirstAttempt) {
        legacyAttemptStorage.markFirstAttempt(stage);
        dispatch({ type: "ATTEMPT_FIRST_FAILURE", stage });
        return { firstAttempt: true };
      }

      dispatch({ type: "ATTEMPT_COMPLETE", stage });
      return { firstAttempt: false };
    } catch (error) {
      await waitForMinimumValidationTime(validationStartedAt);
      dispatch({
        type: "ATTEMPT_SUBMIT_ERROR",
        stage,
        error: t("kyc.errors.stageSubmit"),
      });
      throw error;
    } finally {
      setValidatingStage("");
    }
  };

  const retryStage = (stage) => {
    dispatch({ type: "ATTEMPT_RETRY", stage });
  };

  /**
   * 证件业务流程从这里接收相机结果：
   * 正面拍摄 -> 正面确认/上传 -> 背面准备 -> 背面拍摄 -> 背面确认/上传
   * -> 持久化 document 阶段 -> 首次失败页或进入人脸步骤。
   */
  const handleDocumentCapture = (document) => {
    dispatch({
      type: "SET_DOCUMENT",
      side: state.capture.documentSide,
      value: document,
    });
    dispatch({
      type: "SET_CAPTURE",
      value: { status: "captured", detectionStatus: "GOOD" },
    });
    dispatch({ type: "GO_TO", step: KYC_STEPS.DOCUMENT_REVIEW });
  };

  const handleDocumentCaptureBack = () => {
    if (
      state.capture.documentSide === "back" &&
      state.document.front.previewUrl
    ) {
      dispatch({
        type: "SET_CAPTURE",
        value: { documentSide: "front" },
      });
      dispatch({ type: "GO_TO", step: KYC_STEPS.DOCUMENT_REVIEW });
      return;
    }

    back();
  };

  const handleDocumentRetake = () => {
    const side = state.capture.documentSide;
    revokeLocalPreview(state.document[side]?.previewUrl);
    dispatch({ type: "RESET_DOCUMENT", side });
    dispatch({ type: "SET_CAPTURE", value: { status: "retake" } });
  };

  const restartDocumentCapture = () => {
    revokeLocalPreview(state.document.front?.previewUrl);
    revokeLocalPreview(state.document.back?.previewUrl);
    dispatch({ type: "RESTART_DOCUMENT_CAPTURE" });
  };

  const handleDocumentTypeChange = (documentType) => {
    revokeLocalPreview(state.document.front?.previewUrl);
    revokeLocalPreview(state.document.back?.previewUrl);
    dispatch({ type: "SET_DOCUMENT_TYPE", value: documentType });
  };

  const handleDocumentContinue = async () => {
    const side = state.capture.documentSide;
    const currentDocument = state.document[side];
    if (!currentDocument?.previewUrl || currentDocument.isPlaceholder) return;

    if (side === "front") {
      let uploadCompleted = Boolean(currentDocument.uploadedUrl);
      // 正面确认后先上传，随后进入背面引导；此时还不提交完整 document 阶段。
      try {
        setBusy(true);
        const frontUploadedUrl =
          currentDocument.uploadedUrl ||
          (await legacyUploadAdapter.uploadDocument(
            currentDocument.file,
            currentSession,
          ));
        uploadCompleted = true;
        const document = {
          ...state.document,
          front: { ...currentDocument, uploadedUrl: frontUploadedUrl },
        };

        dispatch({
          type: "SET_DOCUMENT",
          side: "front",
          value: { uploadedUrl: frontUploadedUrl },
        });

        if (requiresDocumentBack) {
          dispatch({
            type: "GO_TO",
            step: KYC_STEPS.DOCUMENT_BACK_PREPARATION,
          });
          return;
        }

        const documentAuthenticity = await checkDocumentAuthenticity(document);

        await persistStage(KYC_ATTEMPT_STAGES.DOCUMENT, {
          ...state,
          document,
          advancedVerification: {
            ...state.advancedVerification,
            [ADVANCED_VERIFICATION_CHECKS.DOCUMENT_AUTHENTICITY]:
              documentAuthenticity,
          },
        });

        legacyTrackingAdapter.documentCompleted();
        dispatch({ type: "GO_TO", step: KYC_STEPS.SELFIE });
      } catch {
        if (!uploadCompleted) {
          dispatch({
            type: "SET_ERROR",
            error: t("kyc.errors.documentUpload"),
          });
        }
      } finally {
        setBusy(false);
      }
      return;
    }

    const { front, back: backDocument } = state.document;
    if (!front.previewUrl || !backDocument.previewUrl) return;

    let uploadCompleted = Boolean(
      front.uploadedUrl && backDocument.uploadedUrl,
    );

    // 背面确认是证件阶段的提交点：保证两面都有远端 URL，再整体持久化订单。
    try {
      setBusy(true);
      const frontUploadedUrl =
        front.uploadedUrl ||
        (await legacyUploadAdapter.uploadDocument(front.file, currentSession));
      const backUploadedUrl =
        backDocument.uploadedUrl ||
        (await legacyUploadAdapter.uploadDocument(
          backDocument.file,
          currentSession,
        ));
      uploadCompleted = true;
      const document = {
        front: { ...front, uploadedUrl: frontUploadedUrl },
        back: { ...backDocument, uploadedUrl: backUploadedUrl },
      };
      dispatch({
        type: "SET_DOCUMENT",
        side: "front",
        value: { uploadedUrl: frontUploadedUrl },
      });
      dispatch({
        type: "SET_DOCUMENT",
        side: "back",
        value: { uploadedUrl: backUploadedUrl },
      });

      const documentAuthenticity = await checkDocumentAuthenticity(document);

      await persistStage(KYC_ATTEMPT_STAGES.DOCUMENT, {
        ...state,
        document,
        advancedVerification: {
          ...state.advancedVerification,
          [ADVANCED_VERIFICATION_CHECKS.DOCUMENT_AUTHENTICITY]:
            documentAuthenticity,
        },
      });

      legacyTrackingAdapter.documentCompleted();
      dispatch({ type: "GO_TO", step: KYC_STEPS.SELFIE });
    } catch (error) {
      if (!uploadCompleted) {
        dispatch({ type: "SET_ERROR", error: t("kyc.errors.documentUpload") });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDocumentBackPreparationContinue = () => {
    if (!requiresDocumentBack) {
      dispatch({ type: "GO_TO", step: KYC_STEPS.SELFIE });
      return;
    }

    dispatch({
      type: "SET_CAPTURE",
      value: {
        status: "idle",
        detectionStatus: "SEARCHING",
        documentSide: "back",
      },
    });
    dispatch({ type: "GO_TO", step: KYC_STEPS.DOCUMENT_CAPTURE });
  };

  const handleFaceContinue = async (completedLiveness = state.liveness) => {
    if (!completedLiveness.completed || !completedLiveness.value) return;

    try {
      setBusy(true);
      const biometricLiveness = await checkBiometricLiveness(completedLiveness);
      const result = await persistStage(KYC_ATTEMPT_STAGES.FACE, {
        ...state,
        liveness: completedLiveness,
        advancedVerification: {
          ...state.advancedVerification,
          [ADVANCED_VERIFICATION_CHECKS.BIOMETRIC_LIVENESS]: biometricLiveness,
        },
      });

      if (result.firstAttempt) return;

      dispatch({
        type: "GO_TO",
        step: KYC_STEPS.ADDRESS_PROOF_PREPARATION,
      });
    } catch {
      // persistStage retains this capture and exposes a retryable error.
    } finally {
      setBusy(false);
    }
  };

  const handleAddressProofContinue = async () => {
    if (!state.addressProof.previewUrl) return;
    let uploadCompleted = Boolean(state.addressProof.uploadedUrl);

    try {
      setBusy(true);
      const uploadedUrl =
        state.addressProof.uploadedUrl ||
        (await legacyUploadAdapter.uploadAddressProof(
          state.addressProof.file,
          currentSession,
        ));
      uploadCompleted = true;
      const addressProof = { ...state.addressProof, uploadedUrl };
      dispatch({ type: "SET_ADDRESS_PROOF", value: { uploadedUrl } });

      const result = await persistStage(KYC_ATTEMPT_STAGES.ADDRESS_PROOF, {
        ...state,
        addressProof,
      });

      if (result.firstAttempt) {
        revokeLocalPreview(state.addressProof.previewUrl);
        return;
      }

      legacyTrackingAdapter.proofCompleted();
      legacyTrackingAdapter.submissionStarted();

      // 无论是否配置返回地址都先进入成功页；SuccessStep 负责展示倒计时并跳转。
      dispatch({ type: "SUBMIT_SUCCESS" });
    } catch (error) {
      if (!uploadCompleted) {
        dispatch({
          type: "SET_ERROR",
          error: t("kyc.errors.addressProofUpload"),
        });
      }
    } finally {
      setBusy(false);
    }
  };

  if (showDesktopHandoff) {
    return <DesktopMobileHandoff mobileUrl={window.location.href} />;
  }

  const step = (() => {
    switch (state.currentStep) {
      case KYC_STEPS.INTRO:
        return <IntroStep onContinue={handleIntroContinue} />;
      case KYC_STEPS.DOCUMENT_TYPE:
        return (
          <DocumentTypeStep
            value={state.documentType}
            allowedTypes={allowedDocumentTypes}
            onChange={handleDocumentTypeChange}
            onBack={back}
            onContinue={next}
          />
        );
      case KYC_STEPS.DOCUMENT_PREPARATION:
        return (
          <DocumentPreparationStep
            side="front"
            documentType={state.documentType}
            onBack={back}
            onContinue={next}
          />
        );
      case KYC_STEPS.DOCUMENT_CAPTURE:
        if (
          state.attempts[KYC_ATTEMPT_STAGES.DOCUMENT].status ===
          KYC_ATTEMPT_STATUS.FAILED_FIRST_ATTEMPT
        ) {
          return (
            <FirstAttemptFailedStep
              stage={KYC_ATTEMPT_STAGES.DOCUMENT}
              onRetry={() => retryStage(KYC_ATTEMPT_STAGES.DOCUMENT)}
            />
          );
        }
        return (
          <DocumentCaptureStep
            side={state.capture.documentSide}
            documentType={state.documentType}
            autoStart
            onBack={handleDocumentCaptureBack}
            onCapture={handleDocumentCapture}
          />
        );
      case KYC_STEPS.DOCUMENT_REVIEW:
        return (
          <DocumentReviewStep
            document={state.document[state.capture.documentSide]}
            side={state.capture.documentSide}
            requiresBack={requiresDocumentBack}
            documentType={state.documentType}
            error={state.error}
            busy={busy}
            onRetake={handleDocumentRetake}
            onContinue={handleDocumentContinue}
          />
        );
      case KYC_STEPS.DOCUMENT_BACK_PREPARATION:
        return (
          <DocumentPreparationStep
            side="back"
            documentType={state.documentType}
            onBack={back}
            onContinue={handleDocumentBackPreparationContinue}
          />
        );
      case KYC_STEPS.SELFIE:
        return (
          <LivenessPreparationStep
            onBack={restartDocumentCapture}
            onContinue={next}
          />
        );
      case KYC_STEPS.LIVENESS:
        if (
          state.attempts[KYC_ATTEMPT_STAGES.FACE].status ===
          KYC_ATTEMPT_STATUS.FAILED_FIRST_ATTEMPT
        ) {
          return (
            <FirstAttemptFailedStep
              stage={KYC_ATTEMPT_STAGES.FACE}
              onRetry={() => retryStage(KYC_ATTEMPT_STAGES.FACE)}
            />
          );
        }
        return (
          <LivenessStep
            session={currentSession}
            liveness={state.liveness}
            completedAttempts={
              state.attempts[KYC_ATTEMPT_STAGES.FACE].completedAttempts
            }
            error={state.error}
            busy={busy}
            onChange={(value) => dispatch({ type: "SET_LIVENESS", value })}
            onBack={restartDocumentCapture}
            onContinue={handleFaceContinue}
          />
        );
      case KYC_STEPS.ADDRESS_PROOF_PREPARATION:
        return (
          <AddressProofPreparationStep
            onBack={returnToLivenessPreparation}
            onContinue={next}
          />
        );
      case KYC_STEPS.ADDRESS_PROOF:
        if (
          state.attempts[KYC_ATTEMPT_STAGES.ADDRESS_PROOF].status ===
          KYC_ATTEMPT_STATUS.FAILED_FIRST_ATTEMPT
        ) {
          return (
            <FirstAttemptFailedStep
              stage={KYC_ATTEMPT_STAGES.ADDRESS_PROOF}
              onRetry={() => retryStage(KYC_ATTEMPT_STAGES.ADDRESS_PROOF)}
            />
          );
        }
        return (
          <AddressProofStep
            addressProof={state.addressProof}
            error={state.error}
            busy={busy}
            onChange={(value) => dispatch({ type: "SET_ADDRESS_PROOF", value })}
            onBack={back}
            onContinue={handleAddressProofContinue}
          />
        );
      case KYC_STEPS.SUBMITTING:
        return <ProcessingStep submitting />;
      case KYC_STEPS.PROCESSING:
        return <ProcessingStep />;
      case KYC_STEPS.SUCCESS:
        return <SuccessStep returnUrl={returnUrl} />;
      case KYC_STEPS.FAILED:
      case KYC_STEPS.RETRY:
        return (
          <FailedStep
            error={state.error}
            onRetry={() =>
              dispatch({ type: "RETRY", step: KYC_STEPS.PROCESSING })
            }
          />
        );
      case KYC_STEPS.EXPIRED:
        return <ExpiredStep />;
      default:
        return <IntroStep onContinue={handleIntroContinue} />;
    }
  })();

  const isIntro = state.currentStep === KYC_STEPS.INTRO;

  return (
    <div className="kyc-flow-shell flex h-full min-h-0 w-full flex-1 items-center justify-center overflow-hidden px-3 py-2 sm:px-6 sm:py-6">
      <div className="kyc-flow-card kyc-surface relative mx-auto flex w-full max-w-2xl flex-col overflow-hidden rounded-[1.5rem] border border-white/10 bg-white sm:rounded-[1.75rem]">
        {!isIntro && (
          <div className="kyc-progress-region shrink-0 border-b border-slate-100 px-4 py-2.5 sm:px-7 sm:py-4">
            <ProgressIndicator currentStep={state.currentStep} />
          </div>
        )}
        <div
          key={state.currentStep}
          className="kyc-flow-content kyc-step-enter flex min-h-0 flex-1 items-start overflow-hidden p-6 sm:p-6"
        >
          {step}
        </div>
        {validatingStage && (
          <VerificationLoadingOverlay stage={validatingStage} />
        )}
        {checkingDevice && <VerificationLoadingOverlay deviceChecking />}
      </div>
    </div>
  );
}
